import { Icon } from "@iconify/react";
import { IconifyIcon } from "@iconify/types";
import homeOutline from "@iconify/icons-eva/home-outline";
import personOutline from "@iconify/icons-eva/person-outline";
import actives from "@iconify-icons/mdi/layers-outline";
import history from "@iconify-icons/mdi/history";
import plus from "@iconify-icons/mdi/plus";
import { matchPath, useLocation } from "react-router-dom";
import { ButtonBase, Paper, useTheme } from "@mui/material";

import { isPatientManagementEnabled } from "../../services/feature-flags.service";
import useGatedNavigate from "../../components/ui/useGatedNavigate";

// ----------------------------------------------------------------------

/** Rotas onde o bottom-nav some (fluxos com a própria navegação) */
const HIDDEN_PREFIXES = [
  "/dashboard/query",
  "/dashboard/dose",
  "/dashboard/doseOral",
  "/dashboard/reviewConsult",
  "/dashboard/reviewOral",
  "/dashboard/cleo",
];

export function shouldShowBottomNav(pathname: string): boolean {
  if (!pathname.startsWith("/dashboard")) return false;
  return !HIDDEN_PREFIXES.some((prefix) => !!matchPath({ path: prefix, end: false }, pathname));
}

interface BottomNavEntry {
  title: string;
  path: string;
  icon: IconifyIcon;
}

export default function BottomNav() {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { navigateGated, gateDialogs } = useGatedNavigate();

  const left: BottomNavEntry[] = [
    { title: "Início", path: "/dashboard/app", icon: homeOutline },
    ...(isPatientManagementEnabled()
      ? [{ title: "Pacientes", path: "/dashboard/patients", icon: personOutline }]
      : []),
  ];
  const right: BottomNavEntry[] = [
    { title: "Ativos", path: "/dashboard/actives", icon: actives },
    { title: "Histórico", path: "/dashboard/history", icon: history },
  ];

  const renderItem = ({ title, path, icon }: BottomNavEntry) => {
    const active = !!matchPath({ path, end: false }, pathname);
    return (
      <ButtonBase
        key={path}
        onClick={() => navigateGated(path)}
        sx={{
          flex: 1,
          height: "100%",
          flexDirection: "column",
          gap: "3px",
          fontFamily: theme.typography.fontFamily,
          fontSize: "10.5px",
          fontWeight: active ? 600 : 500,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
        }}
      >
        <Icon icon={icon} width={21} height={21} />
        {title}
      </ButtonBase>
    );
  };

  return (
    <>
      {gateDialogs}
      <Paper
        square
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          display: "flex",
          alignItems: "center",
          height: "calc(64px + env(safe-area-inset-bottom))",
          pb: "env(safe-area-inset-bottom)",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {left.map(renderItem)}

        <ButtonBase
          aria-label="Nova consulta"
          onClick={() => navigateGated("/dashboard/query")}
          sx={{
            flex: 1,
            height: "100%",
            flexDirection: "column",
            gap: "3px",
            fontFamily: theme.typography.fontFamily,
            fontSize: "10.5px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            color: theme.palette.text.secondary,
          }}
        >
          <Icon icon={plus} width={21} height={21} />
          Consulta
        </ButtonBase>

        {right.map(renderItem)}
      </Paper>
    </>
  );
}
